import { JSX } from 'react';
import { Box, Button, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useSelector, useDispatch } from 'react-redux';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import { useIPE } from 'src/redux/slices/Interview/hooks';
import type { InterviewState } from 'src/redux/slices/Interview/types';
import ContentCard from '../components/ContentCard';
import InterviewFooter from '../components/InterviewFooter';

export default function StaleDocScreen(): JSX.Element {
  const dispatch = useDispatch();
  const { staleDocuments, deferred } = useSelector(
    (state: { interview: InterviewState }) => state.interview
  );
  const { callIPE } = useIPE();
  const currentDoc = staleDocuments[0] ?? null;

  const handleLater = (): void => {
    if (currentDoc) {
      dispatch(interviewActions.deferStaleDoc(currentDoc.survey_file_id));
      dispatch(interviewActions.setLoading(true));
      callIPE({
        skippedQitIds: [...new Set(Object.values(deferred.qitIds).flat())],
      });
    }
  };

  if (!currentDoc) {
    return <Box data-testid="stale-document-screen" />;
  }

  return (
    <Box
      data-testid="stale-document-screen"
      sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
    >
      {/* Body — scrollable, centers content on desktop */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: { md: 'center' },
          alignItems: { md: 'flex-start' },
          pt: { md: 4 },
        }}
      >
        <ContentCard>
          {/* Top block */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Warning badge */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                alignSelf: 'flex-start',
                gap: 0.75,
                bgcolor: 'warning.lighter',
                borderRadius: '20px',
                py: '6px',
                px: '12px',
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.dark' }} />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'warning.dark',
                  lineHeight: 1,
                }}
              >
                Document expired
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.3,
              }}
            >
              Your document is now stale
            </Typography>

            <Typography
              sx={{
                fontSize: 14,
                color: 'text.secondary',
                lineHeight: 1.5,
              }}
            >
              {currentDoc.stale_reason ??
                'The document you uploaded is older than 90 days. Please upload a more recent version to continue.'}
            </Typography>
          </Box>

          {/* Label block */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              {currentDoc.question_text ?? 'Upload document'}
            </Typography>
            {currentDoc.instance_label && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {currentDoc.instance_label}
              </Typography>
            )}
          </Box>

          {/* File card */}
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '16px',
              border: '1px solid',
              borderColor: 'divider',
              p: '20px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              {/* Icon square */}
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: 'error.lighter',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <InsertDriveFileIcon
                  sx={{ fontSize: 20, color: 'error.main' }}
                />
              </Box>

              {/* Meta */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '3px',
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'text.primary',
                    wordBreak: 'break-word',
                  }}
                >
                  {currentDoc.file_name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {currentDoc.uploaded_date
                    ? `Uploaded ${currentDoc.uploaded_date} · Expired`
                    : 'Expired'}
                </Typography>
              </Box>
            </Box>
          </Box>
          {/* Desktop-only inline action buttons */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            <Button
              onClick={handleLater}
              data-testid="stale-later-button-inline"
              sx={{
                height: 44,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'grey.300',
                color: 'text.secondary',
                fontSize: 14,
                fontWeight: 500,
                px: 2.5,
                textTransform: 'none',
              }}
            >
              I&apos;ll do this later
            </Button>
            <Button
              variant="contained"
              color="primary"
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onClick={() => {}} // Shell — upload handler TBD
              data-testid="upload-new-button-inline"
              startIcon={<FileUploadIcon />}
              sx={{
                height: 44,
                borderRadius: '12px',
                fontSize: 16,
                fontWeight: 600,
                px: 3,
                textTransform: 'none',
              }}
            >
              <span>Upload new document</span>
            </Button>
          </Box>
        </ContentCard>
      </Box>

      {/* Mobile-only footer */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <InterviewFooter
          primaryLabel="Upload new document"
          secondaryLabel="I'll do this later"
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onPrimary={() => {}} // Shell — upload handler TBD
          onSecondary={handleLater}
          primaryTestId="upload-new-button"
          secondaryTestId="stale-later-button"
          primaryStartIcon={<FileUploadIcon />}
        />
      </Box>
    </Box>
  );
}
